/**
 * RTK Query API Slice for Recording Management
 * 
 * Handles all recording-related endpoints including:
 * - Recording CRUD operations
 * - File uploads and downloads (with special handling)
 * - Batch operations (download, delete)
 * - Recording analysis and reprocessing
 * - Tagging and metadata management
 * - Report generation
 * - Export functionality
 * 
 * Key architectural decisions:
 * - NO CACHING for PHI data (HIPAA compliance)
 * - Special handling for file uploads (FormData)
 * - Blob response handling for downloads
 * - Large dataset pagination support
 * - External API integration for processing
 * - Maintains snake_case throughout
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import apiClientBaseQuery from './baseQuery';

// Custom base query for file downloads that return blob data
const blobBaseQuery = async (args, api, extraOptions) => {
  const result = await apiClientBaseQuery(
    { ...args, responseType: 'blob' },
    api,
    extraOptions
  );
  
  if (result.error) return result;
  
  // For blob responses, return the raw data
  return { data: result.data.data };
};

export const recordingApi = createApi({
  reducerPath: 'recordingApi',
  baseQuery: apiClientBaseQuery,
  
  // NO CACHING for PHI data (HIPAA compliance)
  keepUnusedDataFor: 0,
  refetchOnMountOrArgChange: true,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  
  // Define cache tags for invalidation
  tagTypes: ['Recording', 'RecordingList', 'RecordingTags', 'RecordingStats'],
  
  endpoints: (builder) => ({
    // Get paginated list of recordings
    getRecordings: builder.query({
      query: (params = {}) => ({
        url: '/v1/all-recordings-pagination',
        method: 'GET',
        params: {
          page: params.page || 1,
          limit: params.limit || 50,
          client_id: params.client_id,
          user_id: params.user_id,
          start_date: params.start_date,
          end_date: params.end_date,
          tags: params.tags,
          search: params.search,
          sort: params.sort || 'date_taken',
          order: params.order || 'desc',
          ...params,
        },
      }),
      transformResponse: (response) => {
        // Handle the recording pagination structure after apiClient normalization
        // apiClient normalizes to: { data: [...], metadata: { total, limit } }        
        const recordings = response.data || [];
        const total = response.metadata?.total || 0;
        const limit = response.metadata?.limit || 50;
                
        return {
          recordings,
          pagination: {
            total: total,
            limit,
            hasMore: recordings.length === limit,
          },
        };
      },
      // No tags for PHI data (no caching)
      providesTags: [],
    }),

    // Get single recording by ID
    getRecordingById: builder.query({
      query: (id) => ({
        url: `/v1/recordings/${id}`,
        method: 'GET',
      }),
      transformResponse: (response) => response.data || response,
      // No caching for PHI
      keepUnusedDataFor: 0,
    }),

    // Upload new recording (special handling for FormData)
    uploadRecording: builder.mutation({
      query: (formData) => ({
        url: '/v1/webupload',
        method: 'POST',
        data: formData, // FormData object with file
        // Let browser set Content-Type with boundary for multipart/form-data
        headers: {},
      }),
      // Force refetch of recordings list after upload
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate any cached lists (though we don't cache PHI)
          dispatch(recordingApi.util.invalidateTags(['RecordingList']));
        } catch {}
      },
    }),

    // Delete recording
    deleteRecording: builder.mutation({
      query: (id) => ({
        url: `/v1/delete-recording/${id}`,
        method: 'DELETE',
      }),
    }),

    // Get recordings by user
    getRecordingsByUser: builder.query({
      query: (userId) => ({
        url: `/v1/recordings/by-user/${userId}`,
        method: 'GET',
      }),
      transformResponse: (response) => response.data || [],
      keepUnusedDataFor: 0,
    }),

    // Get recordings by client/organization
    getRecordingsByClient: builder.query({
      query: (clientId) => ({
        url: `/v1/recordings/by-client/${clientId}`,
        method: 'GET',
      }),
      transformResponse: (response) => response.data || [],
      keepUnusedDataFor: 0,
    }),

    // Reprocess recording file
    reprocessRecording: builder.mutation({
      query: (data) => ({
        url: '/v1/reprocessfile',
        method: 'POST',
        data: data, // Expects: { recording_id, options }
      }),
    }),

    // Download recording (returns blob)
    downloadRecording: builder.query({
      queryFn: async (id, _queryApi, _extraOptions, fetchWithBQ) => {
        const result = await fetchWithBQ({
          url: `/v1/download/${id}`,
          method: 'GET',
          responseType: 'blob',
        });
        
        if (result.error) return { error: result.error };
        
        // Return blob data for file download
        return { data: result.data };
      },
      keepUnusedDataFor: 0,
    }),

    // Batch download recordings (returns zip blob)
    batchDownloadRecordings: builder.mutation({
      queryFn: async (recordingIds, _queryApi, _extraOptions, fetchWithBQ) => {
        const result = await fetchWithBQ({
          url: '/v1/recordings/batch-download',
          method: 'POST',
          data: { recording_ids: recordingIds },
          responseType: 'blob',
        });
        
        if (result.error) return { error: result.error };
        
        return { data: result.data };
      },
    }),

    // Get recording tags
    getRecordingTags: builder.query({
      query: () => ({
        url: '/v1/get-recording-tags',
        method: 'GET',
      }),
      transformResponse: (response) => response.data || [],
      // Tags metadata can be cached briefly
      keepUnusedDataFor: 60,
      providesTags: ['RecordingTags'],
    }),

    // Add tags to recording
    addRecordingTags: builder.mutation({
      query: (data) => ({
        url: '/v1/recordings/add-tags',
        method: 'POST',
        data: data, // Expects: { recording_id, tags: [...] }
      }),
      invalidatesTags: ['RecordingTags'],
    }),

    // Remove tag from recording
    removeRecordingTag: builder.mutation({
      query: (data) => ({
        url: '/v1/recordings/remove-tag',
        method: 'DELETE',
        data: data, // Expects: { recording_id, tag }
      }),
      invalidatesTags: ['RecordingTags'],
    }),

    // Get recording statistics
    getRecordingStatistics: builder.query({
      query: (params = {}) => ({
        url: '/v1/recordings/statistics',
        method: 'GET',
        params: {
          client_id: params.client_id,
          user_id: params.user_id,
          start_date: params.start_date,
          end_date: params.end_date,
          ...params,
        },
      }),
      transformResponse: (response) => response.data || {},
      // Stats can be cached briefly
      keepUnusedDataFor: 60,
      providesTags: ['RecordingStats'],
    }),

    // Generate report from recording
    generateReport: builder.mutation({
      queryFn: async (data, _queryApi, _extraOptions, fetchWithBQ) => {
        const result = await fetchWithBQ({
          url: '/v1/generate_report',
          method: 'POST',
          data: data, // Expects: { recording_id, report_type, options }
          responseType: data.format === 'pdf' ? 'blob' : 'json',
        });
        
        if (result.error) return { error: result.error };
        
        return { data: result.data };
      },
    }),

    // Export recordings data
    exportRecordings: builder.query({
      queryFn: async (params, _queryApi, _extraOptions, fetchWithBQ) => {
        const result = await fetchWithBQ({
          url: '/v1/recordings/export',
          method: 'GET',
          params: {
            format: params.format || 'csv',
            client_id: params.client_id,
            user_id: params.user_id,
            start_date: params.start_date,
            end_date: params.end_date,
            ...params,
          },
          responseType: 'blob',
        });
        
        if (result.error) return { error: result.error };
        
        return { data: result.data };
      },
      keepUnusedDataFor: 0,
    }),

    // Batch delete recordings
    batchDeleteRecordings: builder.mutation({
      query: (recordingIds) => ({
        url: '/v1/recordings/batch-delete',
        method: 'POST',
        data: { recording_ids: recordingIds },
      }),
    }),

    // Get recordings by date range with pagination
    getRecordingsByDateRange: builder.query({
      query: (params) => {
        // Determine which endpoint to use based on role
        let url = '/all-recordings-by-dates-pagination';
        if (params.client_id && !params.user_id) {
          url = '/all-recordings-by-dates-company-pagination';
        } else if (params.user_id) {
          url = '/all-recordings-by-dates-user-pagination';
        }
        
        return {
          url,
          method: 'POST',
          data: {
            start_date: params.start_date,
            end_date: params.end_date,
            query: params.search || params.query || '',
            client_id: params.client_id,
            user_id: params.user_id,
            page: params.page || 1,
          },
        };
      },
      transformResponse: (response) => {
        // Same structure as regular recordings after apiClient normalization
        const recordings = response.data || [];
        const total = response.metadata?.total || 0;
        const limit = response.metadata?.limit || 50;
        
        return {
          recordings,
          pagination: {
            total: total,
            limit,
            hasMore: recordings.length === limit,
          },
        };
      },
      keepUnusedDataFor: 0,
    }),

    // Analyze recording (external API)
    analyzeRecording: builder.mutation({
      query: (data) => ({
        url: '/v1/recordings/analyze',
        method: 'POST',
        data: data, // Expects: { recording_id, analysis_type, options }
      }),
    }),

    // Search recordings by term (legacy endpoint)
    searchRecordingsByTerm: builder.query({
      query: (params) => ({
        url: '/v1/get-search-by-term',
        method: 'GET',
        params: {
          client_id: params.client_id,
          user_id: params.user_id,
          s: params.search_term,
        },
      }),
      transformResponse: (response) => response.data || [],
      keepUnusedDataFor: 0,
    }),

    // Get recording metadata
    getRecordingMetadata: builder.query({
      query: (recordingId) => ({
        url: `/v1/recordings/${recordingId}/metadata`,
        method: 'GET',
      }),
      transformResponse: (response) => response.data || {},
      keepUnusedDataFor: 0,
    }),

    // Update recording metadata
    updateRecordingMetadata: builder.mutation({
      query: ({ recordingId, ...metadata }) => ({
        url: `/v1/recordings/${recordingId}/metadata`,
        method: 'PUT',
        data: metadata,
      }),
    }),

    // Get recording waveform data
    getRecordingWaveform: builder.query({
      query: (recordingId) => ({
        url: `/v1/recordings/${recordingId}/waveform`,
        method: 'GET',
      }),
      transformResponse: (response) => response.data || {},
      keepUnusedDataFor: 0,
    }),
  }),
});

// Export hooks for use in components
export const {
  // Queries
  useGetRecordingsQuery,
  useGetRecordingByIdQuery,
  useGetRecordingsByUserQuery,
  useGetRecordingsByClientQuery,
  useDownloadRecordingQuery,
  useGetRecordingTagsQuery,
  useGetRecordingStatisticsQuery,
  useExportRecordingsQuery,
  useGetRecordingsByDateRangeQuery,
  useSearchRecordingsByTermQuery,
  useGetRecordingMetadataQuery,
  useGetRecordingWaveformQuery,
  
  // Mutations
  useUploadRecordingMutation,
  useDeleteRecordingMutation,
  useReprocessRecordingMutation,
  useBatchDownloadRecordingsMutation,
  useAddRecordingTagsMutation,
  useRemoveRecordingTagMutation,
  useGenerateReportMutation,
  useBatchDeleteRecordingsMutation,
  useAnalyzeRecordingMutation,
  useUpdateRecordingMetadataMutation,
  
  // Lazy queries (for manual triggering)
  useLazyGetRecordingsQuery,
  useLazyDownloadRecordingQuery,
  useLazyExportRecordingsQuery,
  useLazySearchRecordingsByTermQuery,
  
  // For manual cache control if needed
  util: { invalidateTags, resetApiState },
} = recordingApi;

// Helper function for handling file downloads
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export default recordingApi;